/**
 * Report export controls (v1.4.0, Part 6): download the current research
 * report as Markdown or HTML, or open the print-styled HTML and trigger the
 * browser's print dialog (which produces a PDF via "Save as PDF"). All calls
 * go through the authenticated API client.
 */

import React, { useState } from "react";
import { apiClient } from "@/api/client";

function filenameFromDisposition(disposition: string | undefined, fallback: string): string {
  const match = disposition?.match(/filename="([^"]+)"/);
  return match?.[1] ?? fallback;
}

export default function ReportExportButtons({ companyId }: { companyId: string }): React.ReactElement {
  const [busy, setBusy] = useState<null | "markdown" | "html" | "pdf">(null);
  const [error, setError] = useState<string | null>(null);

  const download = async (format: "markdown" | "html"): Promise<void> => {
    setBusy(format);
    setError(null);
    try {
      const response = await apiClient.get(`/companies/${companyId}/research/export`, {
        params: { format },
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data as Blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filenameFromDisposition(
        response.headers["content-disposition"],
        `research.${format === "markdown" ? "md" : "html"}`
      );
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("No report to export yet — generate one first.");
    } finally {
      setBusy(null);
    }
  };

  const printPdf = async (): Promise<void> => {
    setBusy("pdf");
    setError(null);
    try {
      const response = await apiClient.get(`/companies/${companyId}/research/export`, {
        params: { format: "html" },
        responseType: "text",
      });
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        setError("Allow pop-ups to print the report to PDF.");
        return;
      }
      printWindow.document.write(response.data as string);
      printWindow.document.close();
      printWindow.focus();
      // Give the styled document a moment to lay out before invoking print.
      window.setTimeout(() => printWindow.print(), 400);
    } catch {
      setError("No report to export yet — generate one first.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        <button onClick={() => void printPdf()} disabled={busy !== null} className="btn-secondary">
          {busy === "pdf" ? "Preparing…" : "Print / PDF"}
        </button>
        <button onClick={() => void download("html")} disabled={busy !== null} className="btn-secondary">
          {busy === "html" ? "…" : "HTML"}
        </button>
        <button onClick={() => void download("markdown")} disabled={busy !== null} className="btn-secondary">
          {busy === "markdown" ? "…" : "Markdown"}
        </button>
      </div>
      {error && <p className="text-xs text-signal-rose">{error}</p>}
    </div>
  );
}
